// AI Import — parser + validator + converter (PARTE PURA).
//
// Fluxo: texto/json gerado por uma IA (Claude/GPT/Gemini/...)
//   → parseAiResponse(text) → { ok, data, error }
//   → validateAiPayload(data) → { ok, errors[] }
//   → convertToInternal(data) → { tripPayload, timeline, itinerary, hotels,
//                                 expenses, checklistItems, summary }
//
// Schema esperado em .claude/projects/newtrip/ai-prompt-schema.md (v1.2).
//
// IMPORTANTE: este arquivo é 100% PURO — zero imports, sem Firebase, sem
// SDK do Google, sem `import.meta.env`. É de propósito: assim ele roda em
// qualquer ambiente — no browser (Vite), no Node (specs E2E) e no runtime
// do Cloudflare (functions/api/ai/* gera a viagem com IA no backend).
//
// A parte que ESCREVE de fato (cria a trip, salva blobs, resolve coords via
// Places API) vive em `aiImportClient.js` — esse sim só roda no browser.

// ====================================================================
//  1) PARSE — extrai JSON tolerante a ruído ao redor
// ====================================================================

/**
 * Tenta extrair um objeto JSON do texto bruto. Tolerante a:
 *   - JSON puro
 *   - JSON dentro de bloco ```json...``` (caso comum em chat)
 *   - JSON com comentários // ou /* ... *\/ e vírgulas finais
 *   - Texto antes/depois do JSON (extrai entre o primeiro `{` e o último `}`)
 */
// Normaliza o campo `time` de uma parada pra "HH:MM" (24h) — ou string vazia.
//
// POR QUE NO CÓDIGO, e não no schema: o `pattern: '^([01][0-9]|2[0-3]):[0-5]
// [0-9]$'` do responseSchema NÃO é aplicado pela decodificação restrita do
// Gemini (só tipo/enum/required/estrutura são). Em agosto/2026 o modelo
// emitiu `"time": "09:3028.3852, -81.501509:3009:30…"` — coordenadas vazadas
// pra dentro da string, repetindo até estourar o limite de tokens. Aqui a
// garantia é de verdade: o que não casar com HH:MM vira ''.
//
// Aceita e conserta as variações benignas mais comuns: "9:05" → "09:05",
// "09h30" → "09:30", "  14:00  " → "14:00", "14:00:00" → "14:00".
export function sanitizeStopTime(raw) {
  if (typeof raw !== 'string') return '';
  const t = raw.trim();
  if (!t) return '';
  // Ancorado nas DUAS pontas de propósito. "09:3028.3852, -81.5015…" tem um
  // "09:30" no começo, mas o lixo grudado depois significa que o modelo se
  // perdeu no meio do campo — o horário ali não é confiável, e é melhor a
  // parada aparecer sem hora do que com uma hora possivelmente inventada.
  // Sufixo de segundos ("14:00:00") é a única cauda tolerada.
  const m = t.match(/^(\d{1,2})\s*[:hH]\s*(\d{2})(?::\d{2})?$/);
  if (!m) return '';
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return '';
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return '';
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function parseAiResponse(text) {
  if (!text || typeof text !== 'string') {
    return { ok: false, error: 'Arquivo vazio ou ilegível.' };
  }

  // 1.1 — Tenta JSON puro
  try {
    return { ok: true, data: JSON.parse(text) };
  } catch { /* tenta os fallbacks */ }

  // 1.2 — Fallback: extrai bloco ```json ... ```
  const cb = text.match(/```(?:jsonc?|JSONC?)?\s*([\s\S]+?)```/);
  if (cb) {
    const cleaned = stripJsonNoise(cb[1]);
    try {
      return { ok: true, data: JSON.parse(cleaned) };
    } catch (err) {
      return { ok: false, error: `JSON inválido no bloco \`\`\`json: ${err.message}` };
    }
  }

  // 1.3 — Fallback: pega da primeira `{` até a última `}`
  const first = text.indexOf('{');
  const last  = text.lastIndexOf('}');
  if (first >= 0 && last > first) {
    const cleaned = stripJsonNoise(text.slice(first, last + 1));
    try {
      return { ok: true, data: JSON.parse(cleaned) };
    } catch (err) {
      return { ok: false, error: `Não consegui parsear o JSON: ${err.message}` };
    }
  }

  return { ok: false, error: 'Não encontrei nenhum bloco JSON válido no arquivo.' };
}

function stripJsonNoise(s) {
  return String(s)
    .replace(/\/\/[^\n]*/g, '')           // // line comments
    .replace(/\/\*[\s\S]*?\*\//g, '')     // /* block comments */
    .replace(/,(\s*[\]}])/g, '$1')        // vírgulas finais antes de ] ou }
    .trim();
}

// ====================================================================
//  2) VALIDATE — confere campos obrigatórios + shape básico
// ====================================================================

export function validateAiPayload(data) {
  const errors = [];

  if (!data || typeof data !== 'object') {
    return { ok: false, errors: ['Arquivo não contém um objeto JSON.'] };
  }

  // trip
  if (!data.trip || typeof data.trip !== 'object') {
    errors.push('Campo `trip` ausente.');
  } else {
    if (!data.trip.title || typeof data.trip.title !== 'string' || !data.trip.title.trim()) {
      errors.push('`trip.title` ausente ou vazio.');
    }
    if (!data.trip.currency || typeof data.trip.currency !== 'string') {
      errors.push('`trip.currency` ausente.');
    }
  }

  // days (obrigatório como array, pode estar vazio)
  if (!Array.isArray(data.days)) {
    errors.push('`days` deve ser um array.');
  } else {
    data.days.forEach((d, i) => {
      if (typeof d?.n !== 'number') errors.push(`days[${i}].n não é número.`);
      if (d?.stops && !Array.isArray(d.stops)) errors.push(`days[${i}].stops deve ser array.`);
    });
  }

  // stays/transports/expenses são opcionais — só validar se presentes
  if (data.stays !== undefined && !Array.isArray(data.stays)) {
    errors.push('`stays` deve ser um array (ou ausente).');
  }
  if (data.transports !== undefined && !Array.isArray(data.transports)) {
    errors.push('`transports` deve ser um array (ou ausente).');
  }
  if (data.expenses !== undefined && typeof data.expenses !== 'object') {
    errors.push('`expenses` deve ser um objeto (ou ausente).');
  }
  if (data.expenses?.misc !== undefined && !Array.isArray(data.expenses.misc)) {
    errors.push('`expenses.misc` deve ser um array.');
  }

  return { ok: errors.length === 0, errors };
}

// ====================================================================
//  3) CONVERT — schema v1.2 → modelo interno do app
// ====================================================================

// Schema (IA) → enum interno do app (em day.routes[].type).
// IMPORTANTE: o renderer do mapa em `src/features/routes/geometry.js:54`
// reconhece `'air'` (não `'flight'`) pra desenhar a polyline tracejada de
// avião. Manter mappping pra `'air'` aqui é o que faz o trajeto aparecer
// como voo no mapa.
const TRANSPORT_TYPE_MAP = {
  flight:    'air',
  air:       'air',
  train:     'train',
  hstrain:   'hstrain',
  bus:       'bus',
  car:       'car',
  walk:      'car',     // app não tem `walk` em routes — fallback
  boat:      'boat',
  ferry:     'ferry',
  cruise:    'cruise',
  cable_car: 'cable',
  cable:     'cable',
};

// Schema (IA) → enum interno (expense.cat)
const CATEGORY_MAP = {
  food:        'food',
  lodging:     'hotel',
  transport:   'transport',
  attractions: 'activity',
  shopping:    'shopping',
  misc:        'outro',
};

// Emoji por type de stop (vai pro item.emoji e day.icon)
const STOP_EMOJI = {
  restaurant:     '🍽️',
  cafe:           '☕',
  attraction:     '📍',
  viewpoint:      '🌄',
  museum:         '🏛️',
  shopping:       '🛍️',
  activity:       '🎟️',
  break:          '🛋️',
  hotel_checkin:  '🏨',
  hotel_checkout: '🏨',
  transport:      '🚆',
  misc:           '📌',
};

// stops[].type → category (fallback quando cost.category ausente)
const STOP_TYPE_TO_CATEGORY = {
  restaurant:     'food',
  cafe:           'food',
  break:          'food',
  attraction:     'activity',
  museum:         'activity',
  viewpoint:      'activity',
  activity:       'activity',
  shopping:       'shopping',
  hotel_checkin:  'hotel',
  hotel_checkout: 'hotel',
  transport:      'transport',
  misc:           'outro',
};

/**
 * Normaliza `currency_rates` aceitando duas formas:
 *   - objeto: { EUR: 5.85, USD: 5.40 }  (forma natural, escolhida pelo prompt)
 *   - array:  [{ code: 'EUR', rate: 5.85 }, ...]  (Gemini não aceita objetos com
 *     chaves dinâmicas em responseSchema, então o esquema do backend usa array)
 *
 * Sempre devolve um objeto `{ [CODE]: number }` ou null se nada utilizável.
 * Códigos vão pra UPPERCASE. Cotações inválidas/0/negativas são descartadas.
 */
export function normalizeCurrencyRates(raw) {
  if (!raw) return null;
  const out = {};
  let any = false;
  const add = (code, rate) => {
    const c = String(code || '').trim().toUpperCase();
    const r = Number(rate);
    if (!c || !Number.isFinite(r) || r <= 0) return;
    out[c] = r;
    any = true;
  };
  if (Array.isArray(raw)) {
    raw.forEach((entry) => add(entry?.code, entry?.rate));
  } else if (typeof raw === 'object') {
    Object.entries(raw).forEach(([code, rate]) => add(code, rate));
  }
  return any ? out : null;
}

/** Converte o payload da IA em todos os blobs internos do app. */
export function convertToInternal(data, ctx = {}) {
  const tripCurrency = data.trip?.currency || ctx.defaultCurrency || 'BRL';
  // Cotações que a IA devolveu pra esta viagem (R$ por unidade da moeda).
  // O importer cliente persiste em trips/{id}/settings (currencyRates).
  const tripCurrencyRates = normalizeCurrencyRates(data.trip?.currency_rates);

  // Resolve a data ISO de cada day (preenche via start_date + n se data não veio)
  const daysWithDates = (data.days || []).map((d) => {
    const iso = d.date || resolveDayIso(d.n, data.trip?.start_date);
    return { ...d, _iso: iso, _ddmm: iso ? isoToDdmm(iso) : null };
  });

  // ─── 3.1 — Trip payload (pra tripsApi.create) ───
  const tripPayload = {
    name: (data.trip?.title || '').trim(),
    startDate: data.trip?.start_date ? isoToDdmm(data.trip.start_date) : '',
    endDate:   data.trip?.end_date   ? isoToDdmm(data.trip.end_date)   : '',
    datesUndefined: !data.trip?.start_date,
    currency: tripCurrency,
    places: data.trip?.departure_city
      ? [{ name: data.trip.departure_city, country: '', lat: null, lng: null, placeId: '' }]
      : [],
    routeMode: 'ai',
    awaitingImport: false,
  };
  if (data.trip?.summary) tripPayload.summary = data.trip.summary;
  // Checklist vai pra sub-collection `trips/{tripId}/checklist` via
  // createChecklistItem() em importAiPayload — não preenche notes mais.

  // ─── 3.2 — Routes (transports[]) agrupados por day ───
  const routesByDay = {};
  const routeMeta = []; // pra criar expenses linkadas

  (data.transports || []).forEach((t) => {
    const day = daysWithDates.find((d) => d.n === t.day);
    if (!day?._ddmm) return;

    const routeId = genRouteId();
    const type = TRANSPORT_TYPE_MAP[t.type] || 'car';
    const title = t.carrier
      ? `${t.carrier}: ${t.from || '?'} → ${t.to || '?'}`
      : `${t.from || '?'} → ${t.to || '?'}`;

    // Resolve coordenadas: prefere as fornecidas pela IA; fallback pra coords
    // do hotel correspondente (se `to` casa com um stay) ou nulls.
    const fromLat = numOrNull(t.from_lat);
    const fromLng = numOrNull(t.from_lng);
    const toLat   = numOrNull(t.to_lat);
    const toLng   = numOrNull(t.to_lng);

    const route = {
      id: routeId,
      type,
      title,
      from: t.from || '',
      to:   t.to   || '',
      fromLat, fromLng,
      toLat,   toLng,
      departureDate: t.depart_date ? isoToDdmm(t.depart_date) : day._ddmm,
      departureTime: t.depart_time || '',
      arrivalDate:   t.arrive_date ? isoToDdmm(t.arrive_date) : '',
      arrivalTime:   t.arrive_time || '',
      curve: 0,
      color: '',
      lineStyle: 'solid',
      expenseId: null,
      // `estimated:true` propaga do JSON da IA (passagem aérea com valor
      // estimado de mercado quando user não informou preço) — usado pra
      // anotar a expense vinculada com nota "Estimativa de mercado".
      ...(t.estimated === true ? { estimated: true } : {}),
      ...(t.notes ? { note: t.notes } : {}),
    };

    routesByDay[day._ddmm] = routesByDay[day._ddmm] || [];
    routesByDay[day._ddmm].push(route);
    routeMeta.push({ routeId, dayDdmm: day._ddmm, transport: t });
  });

  // ─── 3.3 — Stays → day.hotel (replicado em todos os days entre day_from e day_to-1) ───
  const hotelByDay = {};
  const hotelsList = [];
  const hotelByKey = new Map();    // dedupe key → objeto hotel (com id estável)
  // Qual hotel faz check-in / check-out em cada dia (n). Usado em §3.5 pra
  // os stops hotel_checkin/hotel_checkout linkarem ao hotel via linkedHotelId.
  const hotelCheckinByN = {};
  const hotelCheckoutByN = {};

  (data.stays || []).forEach((stay) => {
    if (!stay.day_from) return;
    const fromDay = daysWithDates.find((d) => d.n === stay.day_from);
    const toDay   = daysWithDates.find((d) => d.n === stay.day_to);
    if (!fromDay?._ddmm) return;

    const name = stay.name || 'Hospedagem';
    const checkin = fromDay._ddmm;
    const checkout = toDay?._ddmm || '';
    const key = `${name}|${checkin}|${checkout}`;

    // Dedupe: stays idênticas compartilham o MESMO objeto (e o mesmo id),
    // pra o linkedHotelId dos itens de check-in/out sempre bater no hotel.
    // O `id` torna o hotel referenciável (itemModal + pin do mapa) — antes
    // hotéis da IA vinham sem id; agora ficam consistentes com os manuais.
    let hotel = hotelByKey.get(key);
    if (!hotel) {
      hotel = {
        id: genHotelId(),
        name,
        address: stay.address || '',
        checkin,
        checkout,
        lat: typeof stay.lat === 'number' ? stay.lat : null,
        lng: typeof stay.lng === 'number' ? stay.lng : null,
        isCruise: stay.type === 'cruise',
        expenseId: null,
      };
      hotelByKey.set(key, hotel);
      hotelsList.push(hotel);
    }

    // Replica em todos os days entre day_from e day_to-1 (último dia = só checkout)
    const lastN = (stay.day_to && stay.day_to > stay.day_from) ? stay.day_to - 1 : stay.day_from;
    for (let n = stay.day_from; n <= lastN; n++) {
      const d = daysWithDates.find((dd) => dd.n === n);
      if (d?._ddmm) hotelByDay[d._ddmm] = hotel;
    }

    // Registra qual hotel faz check-in / check-out em cada dia (n).
    hotelCheckinByN[stay.day_from] = hotel;
    if (stay.day_to) hotelCheckoutByN[stay.day_to] = hotel;
  });

  // ─── 3.3b — Âncoras de raio: coords dos hotéis/stays da viagem ───
  // Usadas pelo guard de 100 km abaixo (§ 3.5). Coletamos de DUAS fontes
  // pra cobrir hotéis sem coord própria no stay mas com coord no objeto
  // já normalizado: o `stay.lat/lng` cru E o `hotel.lat/lng` montado.
  // numOrNull descarta 0/null (0,0 é "ilha nula" no Atlântico — não serve
  // de âncora). Cálculo 100% local — sem nenhuma chamada de API.
  const hotelAnchors = [];
  hotelsList.forEach((h) => {
    const la = numOrNull(h.lat);
    const ln = numOrNull(h.lng);
    if (la !== null && ln !== null) hotelAnchors.push({ lat: la, lng: ln });
  });

  // ─── 3.4 — Timeline (days com routes + hotel) ───
  // IMPORTANTE: `cities` no day deve ser array de OBJETOS {name, country, lat?, lng?},
  // não array de strings. O mapCanvas usa `day.cities[0].name` pra agrupar
  // dias por cidade — sem isso, o setActiveDay não consegue filtrar e o
  // mapa não atualiza ao mudar de dia.
  //
  // Derivação da cidade do dia (NÃO confiar só em `days[].city` — a IA
  // costuma errar nos dias de deslocamento, fixando a cidade de origem):
  //   - Dia COM transporte → cities = [origem, destino] na ordem da viagem
  //     (é um dia de viagem, abrange as DUAS cidades).
  //   - Dia SEM transporte → herda a cidade onde dorme: stay cujo
  //     day_from..day_to-1 cobre o dia; fallback pro destino do último
  //     transporte anterior; fallback pro `days[].city`/stops.
  const dayCitiesByN = deriveDayCities(daysWithDates, data.stays, data.transports);

  const timelineDays = daysWithDates
    .filter((d) => d._ddmm)
    .map((d) => ({
      date: d._ddmm,
      icon: STOP_EMOJI[d.stops?.[0]?.type] || '📍',
      title: d.title || `Dia ${d.n}`,
      details: d.summary || '',
      color: '',
      cities: dayCitiesByN.get(d.n) || [],
      routes: routesByDay[d._ddmm] || [],
      hotel: hotelByDay[d._ddmm] || null,
    }));

  // ─── 3.4b — trip.cities (índice usado pelo mapa pra coords centrais) ───
  // Dedupe por nameKey (lowercase trim). O mapa usa esse array em
  // `buildCityIndex` pra resolver lat/lng da cidade quando o day não tem.
  // Construído a partir das MESMAS cities derivadas por dia, pra ficar
  // consistente com o que o timeline emite.
  const tripCities = [];
  const tripCitiesKeys = new Set();
  daysWithDates.forEach((d) => {
    (dayCitiesByN.get(d.n) || []).forEach((c) => {
      if (!c?.name) return;
      const key = String(c.name).toLowerCase().trim();
      if (!key || tripCitiesKeys.has(key)) return;
      tripCitiesKeys.add(key);
      tripCities.push({
        name: c.name,
        country: c.country || '',
        ...(typeof c.lat === 'number' && typeof c.lng === 'number'
          ? { lat: c.lat, lng: c.lng }
          : {}),
      });
    });
  });
  if (tripCities.length) tripPayload.cities = tripCities;

  // ─── 3.5 — Itinerary (items por day, no overrides) ───
  const itineraryOverrides = {};
  daysWithDates.forEach((d) => {
    if (!d._ddmm || !d.stops?.length) return;
    const items = d.stops.map((stop) => {
      // Coords + placeId do stop: a IA já manda lat/lng nos stops (mesmo
      // shape que `inferCityCoords` lê). Anexamos no item pra ele poder
      // virar pin na camada "Itinerário" do mapa. placeId é opcional —
      // schema usa snake_case (`place_id`), mas aceitamos camelCase tb.
      let lat = numOrNull(stop.lat);
      let lng = numOrNull(stop.lng);
      let placeId = stop.place_id || stop.placeId || '';
      // Endereço do stop: o schema v1.2 usa `location` (endereço descritivo,
      // ex: "Bahnhofstrasse, Zürich"). Aceitamos também `address` e
      // `formatted_address` como aliases — algumas IAs (ou prompts variantes)
      // emitem em camelCase ou no formato Places. O itemModal lê de
      // `initial.address` pra preencher o campo "Local no mapa", então sem
      // esse campo o user precisa pesquisar o lugar de novo na mão (mesmo
      // já tendo lat/lng/placeId do JSON).
      let stopAddress = stop.address || stop.location || stop.formatted_address || '';

      // ── Guard de raio (100 km) ──────────────────────────────────
      // Se a IA chutou uma coord longe de qualquer hotel da viagem, a
      // parada é provavelmente um pin bogus — descartamos a localização
      // (lat/lng/placeId/address) e deixamos como item sem ponto no mapa.
      // EXCEÇÃO: check-in/check-out de hotel SEMPRE mantêm a coord — eles
      // SÃO a âncora (e podem legitimamente ficar fora do raio de outros
      // hotéis numa viagem multi-cidade). Cálculo 100% local — sem API.
      const isHotelAnchorStop = stop.type === 'hotel_checkin'
        || stop.type === 'hotel_checkout';
      if (!isHotelAnchorStop
          && !isStopLocationPlausible(lat, lng, hotelAnchors)) {
        lat = null;
        lng = null;
        placeId = '';
        stopAddress = '';
      }

      // ── Vínculo de hotel ────────────────────────────────────────
      // Stops de check-in/check-out viram itens type='hotel' VINCULADOS ao
      // hotel correspondente (linkedHotelId) — sem pin/endereço próprios:
      // são representados pelo pin do hotel no mapa. Espelha o comportamento
      // do itemModal pra itens do tipo hotel. Se não houver hotel casando
      // (IA emitiu o stop sem stay), cai no comportamento antigo (coords).
      const linkedHotel = stop.type === 'hotel_checkin'  ? hotelCheckinByN[d.n]
                        : stop.type === 'hotel_checkout' ? hotelCheckoutByN[d.n]
                        : null;
      const linkedHotelId = linkedHotel?.id || '';

      return {
        id: genItemId(d._ddmm),
        time: sanitizeStopTime(stop.time),
        emoji: STOP_EMOJI[stop.type] || '📍',
        title: String(stop.title || 'Parada').slice(0, 120),
        // Stops de hotel viram itens type='hotel' (ícone + UI corretos).
        ...(isHotelAnchorStop ? { type: 'hotel' } : {}),
        details: serializeStopDetails(
          stop,
          (lat !== null && lng !== null && !linkedHotelId) ? { lat, lng } : null,
        ),
        // Vinculado a um hotel → sem coords próprias (usa o pin do hotel).
        // Senão, mantém lat/lng/placeId/address do stop como antes.
        ...(linkedHotelId
          ? { linkedHotelId }
          : {
              ...(lat !== null && lng !== null ? { lat, lng } : {}),
              ...(placeId ? { placeId } : {}),
              ...(stopAddress ? { address: stopAddress } : {}),
            }),
      };
    });
    itineraryOverrides[d._ddmm] = {
      dayTitle: d.title || '',
      dayIcon:  STOP_EMOJI[d.stops[0]?.type] || '📍',
      color: '',
      items,
    };
  });

  // ─── 3.6 — Expenses (derivadas + misc) ───
  const expensesOverrides = {};
  const otherExpenses = [];

  // 3.6a — Stops com cost.
  // Pula entradas com amount null/0 — a IA pode emitir cost:{amount:null} pra
  // marcar "grátis/incluso/sem preço"; criar expense de $0 polui a carteira.
  daysWithDates.forEach((d) => {
    if (!d._ddmm || !d.stops) return;
    d.stops.forEach((stop) => {
      if (!stop.cost) return;
      const amount = Number(stop.cost.amount);
      if (!Number.isFinite(amount) || amount <= 0) return;
      const cat = CATEGORY_MAP[stop.cost.category]
        || STOP_TYPE_TO_CATEGORY[stop.type]
        || 'outro';
      const exp = {
        id: genExpenseId(),
        kind: 'saida',
        desc: stop.title || 'Parada',
        planned: amount,
        actual: null,
        currency: stop.cost.currency || tripCurrency,
        cat,
        date: d._ddmm,
        linkedTo: null,
        installments: 1,
      };
      expensesOverrides[d._ddmm] = expensesOverrides[d._ddmm] || [];
      expensesOverrides[d._ddmm].push(exp);
    });
  });

  // 3.6b — Stays (total_cost prefer, fallback price_per_night × noites).
  // Pula stays com amount null/0 (hotel sem preço informado — user preenche
  // depois). Resultado linka via `linkedTo: {type:'hotel'}` pra carteira
  // mostrar o ícone de gasto-vinculado.
  (data.stays || []).forEach((stay) => {
    if (!stay.day_from) return;
    const fromDay = daysWithDates.find((d) => d.n === stay.day_from);
    if (!fromDay?._ddmm) return;

    let amount = 0;
    let currency = tripCurrency;
    if (stay.total_cost?.amount) {
      amount = Number(stay.total_cost.amount) || 0;
      currency = stay.total_cost.currency || tripCurrency;
    } else if (stay.price_per_night?.amount && stay.day_to) {
      const nights = Math.max(1, (stay.day_to - stay.day_from));
      amount = (Number(stay.price_per_night.amount) || 0) * nights;
      currency = stay.price_per_night.currency || tripCurrency;
    }
    if (!Number.isFinite(amount) || amount <= 0) return;

    expensesOverrides[fromDay._ddmm] = expensesOverrides[fromDay._ddmm] || [];
    expensesOverrides[fromDay._ddmm].push({
      id: genExpenseId(),
      kind: 'saida',
      desc: stay.name || 'Hospedagem',
      planned: amount,
      actual: null,
      currency,
      cat: 'hotel',
      date: fromDay._ddmm,
      linkedTo: { type: 'hotel', dayDate: fromDay._ddmm },
      installments: 1,
    });
  });

  // 3.6c — Transports (linkados a route via routeMeta)
  // Pula transportes com amount null/0 — pode ocorrer pra trens internacionais
  // ou cruzeiros sem baseline confiável. Passagens aéreas, porém, devem vir
  // com estimativa de mercado OBRIGATÓRIA do prompt da IA (flag `estimated:
  // true`), então em geral cairão aqui. Logamos warning quando vier flight
  // sem valor — sinal de que a IA não seguiu a instrução do prompt e o user
  // vai ter que preencher o preço do voo na mão depois.
  // Quando `estimated:true`, propagamos pra `note` da expense pra sinalizar
  // que o valor é estimativa de mercado.
  routeMeta.forEach(({ routeId, dayDdmm, transport }) => {
    if (!transport.cost) {
      if (transport?.type === 'flight' || transport?.type === 'air') {
        console.warn('[aiImport] Voo sem cost — IA não estimou a passagem:', transport?.from, '→', transport?.to);
      }
      return;
    }
    const amount = Number(transport.cost.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      if (transport?.type === 'flight' || transport?.type === 'air') {
        console.warn('[aiImport] Voo com amount null/0 — IA não estimou a passagem:', transport?.from, '→', transport?.to, '(amount:', transport.cost.amount, ')');
      }
      return;
    }
    const isEstimated = transport.estimated === true;
    const baseNote = (transport.notes || '').trim();
    const estNote = isEstimated ? 'Estimativa de mercado' : '';
    const note = [estNote, baseNote].filter(Boolean).join(' — ');
    expensesOverrides[dayDdmm] = expensesOverrides[dayDdmm] || [];
    expensesOverrides[dayDdmm].push({
      id: genExpenseId(),
      kind: 'saida',
      desc: transport.carrier
        ? `${transport.carrier}: ${transport.from || ''} → ${transport.to || ''}`
        : `${transport.from || ''} → ${transport.to || ''}`,
      planned: amount,
      actual: null,
      currency: transport.cost.currency || tripCurrency,
      cat: 'transport',
      date: dayDdmm,
      linkedTo: { type: 'route', dayDate: dayDdmm, routeId },
      installments: 1,
      ...(isEstimated ? { estimated: true } : {}),
      ...(note ? { note } : {}),
    });
  });

  // 3.6d — expenses.misc[] → overrides (se day) ou otherExpenses (se não).
  // Pula entradas com amount null/0 — misc com valor nulo é só ruído.
  (data.expenses?.misc || []).forEach((m) => {
    const amount = Number(m.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const cat = CATEGORY_MAP[m.category] || 'outro';
    const exp = {
      id: genExpenseId(),
      kind: 'saida',
      desc: m.desc || 'Gasto',
      planned: amount,
      actual: null,
      currency: m.currency || tripCurrency,
      cat,
      date: null,
      linkedTo: null,
      installments: 1,
    };
    if (m.day) {
      const d = daysWithDates.find((dd) => dd.n === m.day);
      if (d?._ddmm) {
        exp.date = d._ddmm;
        expensesOverrides[d._ddmm] = expensesOverrides[d._ddmm] || [];
        expensesOverrides[d._ddmm].push(exp);
        return;
      }
    }
    otherExpenses.push(exp);
  });

  // ─── 3.7 — Checklist items (vai pra sub-collection no importer) ───
  //  - `todo_before[]` vira itens diretos
  //  - `documents[]` vira "Adicionar documento: <nome>"
  const checklistItems = [];
  (data.checklist?.todo_before || []).forEach((todo) => {
    const desc = String(todo || '').trim();
    if (desc) checklistItems.push({ description: desc, dueDate: null });
  });
  (data.checklist?.documents || []).forEach((doc) => {
    const desc = String(doc || '').trim();
    if (desc) checklistItems.push({ description: `Adicionar documento: ${desc}`, dueDate: null });
  });

  const _ts = Date.now();
  return {
    tripPayload,
    // Cotações que a IA devolveu — persistidas em trip settings pelo importer
    // cliente (importAiPayload). Pode ser null se a IA não retornou rates.
    currencyRates: tripCurrencyRates,
    timeline:  { days: timelineDays, _ts },
    itinerary: { overrides: itineraryOverrides, _ts },
    hotels:    { hotels: hotelsList, _ts },
    expenses:  { overrides: expensesOverrides, otherExpenses, _ts },
    checklistItems,
    summary: {
      days: timelineDays.length,
      stops: daysWithDates.reduce((n, d) => n + (d.stops?.length || 0), 0),
      stays: hotelsList.length,
      transports: routeMeta.length,
      expenses:
        Object.values(expensesOverrides).reduce((n, arr) => n + arr.length, 0)
        + otherExpenses.length,
      checklist: checklistItems.length,
    },
  };
}

// ====================================================================
//  Helpers
// ====================================================================

function isoToDdmm(iso) {
  const m = String(iso || '').match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : '';
}

function resolveDayIso(n, startIso) {
  if (!startIso || !n) return null;
  const [y, mo, d] = startIso.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  dt.setDate(dt.getDate() + (n - 1));
  const pad = (x) => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/**
 * Serializa notes + metadados (duração/coords) pro campo livre `details`.
 *
 * `coords` é opcional: quando o guard de raio descarta a localização da
 * parada, passamos `null` aqui pra que as coords bogus NÃO vazem pro texto
 * de notas (caso contrário o user veria um lat/lng inválido nas Notas mesmo
 * com o pin removido). Sem `coords`, cai no lat/lng cru do stop.
 */
function serializeStopDetails(stop, coords) {
  const parts = [];
  if (stop.notes) parts.push(stop.notes);
  const meta = [];
  // NOTA: NÃO incluir `stop.location` aqui — o endereço agora é persistido
  // no campo estruturado `item.address` (lido pelo itemModal no slot "Local
  // no mapa"). Deixar também em `details` duplicaria a info pro user.
  if (stop.duration_min)  meta.push(`⏱ ${stop.duration_min}min`);
  const lat = coords === undefined
    ? (typeof stop.lat === 'number' ? stop.lat : null)
    : (coords ? coords.lat : null);
  const lng = coords === undefined
    ? (typeof stop.lng === 'number' ? stop.lng : null)
    : (coords ? coords.lng : null);
  if (typeof lat === 'number' && typeof lng === 'number') {
    meta.push(`${lat.toFixed(4)},${lng.toFixed(4)}`);
  }
  if (meta.length) parts.push(meta.join(' · '));
  return parts.join('\n\n');
}

/**
 * Tenta inferir as coords do "centro" da cidade do dia a partir do primeiro
 * stop que tem lat/lng válidos. Usado como último fallback pra coords de
 * `day.cities[].lat/lng`, que o mapa do app usa pra agrupar dias por
 * cidade e centralizar a câmera ao mudar de dia.
 */
function inferCityCoords(day) {
  for (const stop of day?.stops || []) {
    const lat = numOrNull(stop?.lat);
    const lng = numOrNull(stop?.lng);
    if (lat !== null && lng !== null) return { lat, lng };
  }
  return null;
}

/**
 * Normaliza um nome de cidade que pode vir com código entre parênteses
 * (ex: "Belo Horizonte (BH)", "São Paulo (GRU)"). O mapa agrupa por nome,
 * então tirar o sufixo de aeroporto evita criar grupos duplicados.
 */
function cleanCityName(raw) {
  return String(raw || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/** Monta um objeto city {name, country, lat?, lng?} descartando coords inválidas. */
function makeCity(name, country, lat, lng) {
  const cleanName = cleanCityName(name);
  if (!cleanName) return null;
  const city = { name: cleanName, country: country || '' };
  const la = numOrNull(lat);
  const ln = numOrNull(lng);
  if (la !== null && ln !== null) { city.lat = la; city.lng = ln; }
  return city;
}

/**
 * Deriva as cidades de CADA dia (Map de `n` → array de city objects).
 *
 * Sinais confiáveis no JSON da IA, em ordem de prioridade:
 *   1. `transports[]` — um transporte no dia N de A→B significa que o dia N
 *      abrange AS DUAS cidades [A, B] (dia de deslocamento).
 *   2. `stays[]` — a hospedagem cujo intervalo day_from..day_to-1 cobre o dia
 *      diz em qual cidade você dorme (dias parados).
 *   3. `days[].city` — dica explícita da IA pro próprio dia. Confiável
 *      quando NÃO há transporte/stay estruturado cobrindo o dia (trips
 *      "manuais"). Tem prioridade sobre herança: o dia 2 de um road trip
 *      tem city própria ("Florianópolis") e não deve herdar a do dia 1.
 *   4. destino do último transporte/stay anterior — herda só quando o dia
 *      não tem NENHUM sinal próprio (nem transporte, nem stay, nem city).
 */
function deriveDayCities(daysWithDates, stays, transports) {
  const result = new Map();
  const transportsList = Array.isArray(transports) ? transports : [];
  const staysList = Array.isArray(stays) ? stays : [];

  // Último destino conhecido conforme avançamos pelos dias — usado pra
  // herdar a cidade em dias sem stay nem transporte.
  let lastDestination = null;

  for (const d of daysWithDates) {
    const cities = [];
    const seen = new Set();
    const pushCity = (city) => {
      if (!city) return;
      const key = city.name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      cities.push(city);
    };

    // 1) Transportes do dia → origem + destino, na ordem da viagem.
    const dayTransports = transportsList.filter((t) => t.day === d.n);
    for (const t of dayTransports) {
      pushCity(makeCity(t.from, d.country, t.from_lat, t.from_lng));
      const dest = makeCity(t.to, d.country, t.to_lat, t.to_lng);
      pushCity(dest);
      if (dest) lastDestination = dest;
    }

    // 2) Stay que cobre o dia. Cobre day_from..day_to INCLUSIVE do checkout:
    //    no dia de checkout você ainda está naquela cidade de manhã. Se
    //    houver transporte nesse dia, o passo 1 já tratou (origem+destino)
    //    antes deste. (O hotel/marker é replicado só até day_to-1 em outro
    //    ponto do código — aqui é cidade, não hospedagem.)
    if (!cities.length) {
      const stay = staysList.find((s) => {
        if (typeof s.day_from !== 'number') return false;
        const lastDay = (typeof s.day_to === 'number' && s.day_to > s.day_from)
          ? s.day_to
          : s.day_from;
        return d.n >= s.day_from && d.n <= lastDay;
      });
      if (stay) {
        const stayCity = makeCity(stay.city, d.country, stay.lat, stay.lng);
        if (stayCity) {
          pushCity(stayCity);
          lastDestination = stayCity;
        }
      }
    }

    // 3) `days[].city` próprio do dia — tem prioridade sobre herança. A IA
    //    erra `days[].city` em dias COBERTOS por stay/transporte (passos 1-2
    //    já trataram esses), mas pra um dia sem sinal estruturado a dica
    //    própria é melhor que herdar a cidade do dia anterior.
    if (!cities.length && d.city) {
      const coords = inferCityCoords(d);
      const own = makeCity(d.city, d.country, coords?.lat, coords?.lng);
      if (own) {
        pushCity(own);
        lastDestination = own;
      }
    }

    // 4) Último recurso: herda o destino do último transporte/stay/city
    //    anterior — só quando o dia não tem NENHUM sinal próprio.
    if (!cities.length && lastDestination) {
      pushCity(makeCity(
        lastDestination.name,
        lastDestination.country || d.country,
        lastDestination.lat,
        lastDestination.lng,
      ));
    }

    result.set(d.n, cities);
  }

  return result;
}

function numOrNull(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

// ─── Guard de raio (100 km) — math local, SEM API ────────────────
//
// A IA chuta lat/lng e às vezes anexa uma parada a um lugar muito longe do
// destino real (pin no meio do nada). Como salvaguarda, ancoramos no(s)
// hotel(éis) da viagem: uma parada legítima fica perto de onde se dorme.
//
// IMPORTANTE: tudo é cálculo local (fórmula de haversine sobre o lat/lng
// que já vem no JSON). NÃO chama Geocoding/Places/Directions — o user já
// teve conta cara de API do Google e o import precisa ser de custo zero.

export const MAX_STOP_DISTANCE_KM = 100;

/** Distância great-circle (haversine) em km entre dois pares {lat,lng}. */
export function haversineKm(a, b) {
  const R = 6371; // raio médio da Terra em km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/**
 * Decide se uma coordenada de parada é "plausível" — i.e. está dentro de
 * MAX_STOP_DISTANCE_KM de PELO MENOS UM hotel/stay da viagem.
 *
 * Edge cases (decisões documentadas):
 *  - Sem coords (lat/lng null): retorna `true` — não há nada a descartar,
 *    a parada simplesmente segue sem localização.
 *  - Trip SEM nenhum hotel com coord: retorna `true` — sem âncora não dá
 *    pra definir um raio; descartar tudo seria pior que aceitar como está.
 *    (Itinerários sem hotel são raros mas válidos; o prompt já exige hotel
 *    todo dia, então cair aqui é exceção.)
 *
 * @param {number|null} lat
 * @param {number|null} lng
 * @param {Array<{lat:number,lng:number}>} hotelCoords  hotéis com coord válida
 * @returns {boolean} true = mantém a localização; false = descarta
 */
export function isStopLocationPlausible(lat, lng, hotelCoords) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return true; // sem coord — nada a descartar
  }
  if (!Array.isArray(hotelCoords) || hotelCoords.length === 0) {
    return true; // sem âncora — não dá pra aplicar o raio
  }
  const stop = { lat, lng };
  return hotelCoords.some((h) => haversineKm(stop, h) <= MAX_STOP_DISTANCE_KM);
}

function genItemId(ddmm) {
  return `it${(ddmm || '').replace('/', '')}-${Date.now()}${Math.floor(Math.random() * 1000)}`;
}
// Mesmo formato de newHotelId() em roteiro/api.js — hotéis da IA ficam
// com id consistente com os criados manualmente (linkedHotelId, upsert).
function genHotelId() {
  return 'h_' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-3);
}
function genExpenseId() {
  return `u-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}
function genRouteId() {
  return `r-${Date.now()}${Math.floor(Math.random() * 10000)}`;
}
